import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material"
import { Autocomplete, Box, Checkbox, TextField } from "@mui/material"
import { ChangeEvent, FC } from "react"
import { Controller } from "react-hook-form"

export interface AutocompleteOption {
  label: string
  value: any
}
interface AutocompleteWrapperProps {
  options: any[]
  label?: string
  placeholder: string
  fullWidth?: boolean
  uniquekey?: string
  size?: "small" | "medium"
  multiple?: boolean
  disabled?: boolean
  margin?: "none" | "normal" | "dense" | undefined

  value?: any
  onChange?: any
  required?: boolean

  name: string
  control?: any
  rules?: any
  "data-testid"?: string
}

export const AutocompleteWrapper: FC<AutocompleteWrapperProps> = ({
  fullWidth = true,
  label = "",
  required = false,
  size = "medium",
  multiple = false,
  placeholder = "",
  disabled = false,
  margin = "normal",
  ...props
}) => {
  const icon = <CheckBoxOutlineBlank fontSize="small" />
  const checkedIcon = <CheckBox fontSize="small" />

  const isOptionEqualToValue = (
    option: AutocompleteOption,
    selectedOption: any,
  ) => {
    if (!option || selectedOption === null) return false
    if (props?.uniquekey) {
      return (
        option.value?.[props.uniquekey] === selectedOption?.[props.uniquekey]
      )
    }
    return option.value === selectedOption
  }

  const getOptionLabel = (option: AutocompleteOption) => {
    if (typeof option === "string") {
      return option
    }
    if (!option?.label && props.options.length) {
      const selectedOption = props.options.find((e) =>
        isOptionEqualToValue(e, option),
      ) as AutocompleteOption
      return selectedOption?.label || ""
    }
    return option?.label || ""
  }

  if (props?.control) {
    return (
      <Controller
        name={props.name}
        control={props.control}
        rules={props.rules}
        render={({
          field: { value, onChange: fieldOnChange, ref },
          fieldState: { error: fieldError },
        }) => (
          <Autocomplete
            multiple={multiple}
            disableCloseOnSelect
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            filterSelectedOptions
            onChange={(_, selectedOption) => {
              if (multiple) {
                // TODO: ADD logic for multiple options
              } else {
                if (typeof selectedOption === "string") {
                  fieldOnChange(selectedOption)
                } else {
                  fieldOnChange((selectedOption as AutocompleteOption)?.value)
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size={size}
                label={label}
                placeholder={placeholder}
                margin="normal"
                variant="outlined"
                error={!!fieldError}
                helperText={fieldError ? fieldError.message : undefined}
                inputRef={ref}
              />
            )}
            {...props}
          />
        )}
      />
    )
  }
  return (
    <Autocomplete
      {...props}
      multiple={multiple}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      value={props.value || (multiple ? [] : null)}
      onChange={(_, newValue) => {
        if (multiple) {
          const optionList = newValue as AutocompleteOption[]
          // for selected option value is array of number , string
          props.onChange(optionList.map((option) => option?.value ?? option))
        } else {
          const option = newValue as AutocompleteOption
          props.onChange(option?.value ?? null)
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={placeholder}
          margin={margin}
          variant="outlined"
          required={required}
          onChange={(
            newValue: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            if (!newValue.target.value && !multiple) {
              props.onChange("")
            }
          }}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props
        return (
          <Box component={"li"} key={key} {...optionProps}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {getOptionLabel(option)}
          </Box>
        )
      }}
    />
  )
}
